import { mdiAlertCircle, mdiArrowLeft, mdiEye, mdiEyeOff, mdiLightningBolt, mdiThumbDown, mdiThumbUp, } from "@mdi/js";
import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { shouldShowPremiumAd } from "../../../util/selectors";
import { Button } from "../../../ui/components/button/Button";
import { Icon } from "../../../ui/components/icon/Icon";
import { Pictogram } from "../../../ui/components/pictogram/Pictogram";
import { Typography, TypographyLink, } from "../../../ui/components/typography/Typography";
import { opn } from "../../../util/api";
import { log } from "../../../util/log";
import { Campaign, Content, Section, nexusModsURL, } from "../../../util/util";
import MainPage from "../../../views/MainPage";
import { unknownToError } from "@vortex/shared";
import { HealthCheckFeedbackEvent } from "../../analytics/mixpanel/MixpanelEvents";
import { PREMIUM_PATH } from "../../nexus_integration/constants";
import { setRequirementHidden, setFeedbackGiven } from "../actions/persistent";
import { FeedbackModal } from "../components/feedback_modal";
import { ModRequirement } from "../components/mod_requirement";
import { PremiumModal } from "../components/premium_modal";
import { getModFiles, hiddenRequirements, feedbackGivenMap, } from "../selectors";
import { getModFilesWithCache } from "../util";
function HealthCheckDetailPage({ mod, api, onBack, onDownloadMod, }) {
    const { t } = useTranslation(["health_check", "common"]);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    // Check if feedback was already given for this requirement (persisted)
    const feedbackMap = useSelector(feedbackGivenMap);
    const givenFeedBack = React.useMemo(() => {
        const given = feedbackMap[mod.requiredBy.modId] || [];
        return given.includes(mod.id);
    }, [feedbackMap, mod.requiredBy.modId, mod.id]);
    // Get mod files from Redux cache
    const modFiles = useSelector((state) => getModFiles(state, mod.modId));
    const showPremiumAd = useSelector(shouldShowPremiumAd);
    // Check if this requirement is currently hidden
    const hiddenReqsMap = useSelector(hiddenRequirements);
    const isHidden = React.useMemo(() => {
        const hiddenReqs = hiddenReqsMap[mod.requiredBy.modId] || [];
        return hiddenReqs.includes(mod.id);
    }, [hiddenReqsMap, mod.requiredBy.modId, mod.id]);
    // Fetch mod files when component mounts using the requirement's gameId
    useEffect(() => {
        if (mod.externalRequirement) {
            return;
        }
        getModFilesWithCache(api, mod.gameId, mod.modId).catch((err) => {
            log("error", "Failed to fetch mod files:", unknownToError(err));
        });
    }, [api, mod.gameId, mod.modId]);
    // Memoized callback for opening the requiring mod's page
    const openRequiringModPage = React.useCallback(() => {
        if (!mod.requiredBy.modUrl) {
            return;
        }
        opn(mod.requiredBy.modUrl).catch(() => null);
    }, [mod.requiredBy.modUrl]);
    // Memoized callback for premium modal download action
    const handleDownload = React.useCallback(async (file) => {
        setShowPremiumModal(false);
        if (!showPremiumAd) {
            await onDownloadMod?.(mod, file);
            onBack();
            // Health check list is refreshed automatically by the debounced
            // did-install-mod / did-enable-mods triggers in api/triggers.ts
        }
        else {
            setShowPremiumModal(true);
        }
    }, [onDownloadMod, mod, showPremiumAd, onBack]);
    // Memoized callback for positive feedback (thumbs up)
    const handlePositiveFeedback = React.useCallback(() => {
        api.store?.dispatch(setFeedbackGiven(mod.requiredBy.modId, mod.id));
        api.events.emit("analytics-track-mixpanel-event", new HealthCheckFeedbackEvent("positive", mod.gameId, mod.modId, mod.requiredBy.modId));
    }, [api, mod]);
    // Memoized callback for negative feedback (from modal)
    const handleFeedbackSuccess = React.useCallback((reasons) => {
        api.store?.dispatch(setFeedbackGiven(mod.requiredBy.modId, mod.id));
        api.events.emit("analytics-track-mixpanel-event", new HealthCheckFeedbackEvent("negative", mod.gameId, mod.modId, mod.requiredBy.modId, reasons));
        setShowFeedbackModal(false);
    }, [api, mod]);
    // Memoized callback for toggling hide/unhide state
    const handleToggleHide = React.useCallback(() => {
        // Toggle the hidden state
        api.store?.dispatch(setRequirementHidden(mod.requiredBy.modId, mod.id, !isHidden));
        // Navigate back to the main health check page
        onBack();
    }, [api.store, mod.requiredBy.modId, mod.id, isHidden, onBack]);
    // Memoized callback for confirming external requirement installation
    const handleConfirmInstall = React.useCallback(() => {
        // Hide this requirement from future checks
        api.store?.dispatch(setRequirementHidden(mod.requiredBy.modId, mod.id, true));
        // Navigate back to the main health check page
        onBack();
    }, [api.store, mod.requiredBy.modId, mod.id, onBack]);
    const goPremium = React.useCallback(() => {
        opn(nexusModsURL(PREMIUM_PATH, {
            section: Section.Users,
            campaign: Campaign.BuyPremium,
            content: Content.HealthCheckAd,
        })).catch(() => undefined);
    }, []);
    return (React.createElement(MainPage, { id: "health-check-detail-page" },
        React.createElement(MainPage.Body, null,
            React.createElement("div", { className: "space-y-6 p-6" },
                React.createElement("div", { className: "flex items-center justify-between gap-x-6" },
                    React.createElement("div", { className: "flex grow items-center gap-x-2" },
                        React.createElement(Pictogram, { name: "health-check", size: "sm" }),
                        React.createElement("div", { className: "grow" },
                            React.createElement("div", { className: "flex items-center gap-x-1.5" },
                                React.createElement(Typography, { as: "h2", className: "m-0", typographyType: "heading-xs" }, t("detail::title")),
                                React.createElement(Typography, { as: "div", className: "justity-center flex min-h-4 items-center rounded-sm border border-neutral-strong px-1 leading-4", typographyType: "title-xs" }, t("common:::beta"))),
                            React.createElement(Typography, { appearance: "moderate" }, t("detail::subtitle")))),
                    React.createElement("div", null,
                        React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiArrowLeft, size: "sm", onClick: onBack }, t("common:::view_all")))),
                showPremiumAd && (React.createElement("div", { className: "mb-4 flex items-center justify-between gap-x-6 rounded-sm border border-premium-moderate/23 bg-linear-to-r from-premium-moderate/25 via-premium-moderate/10 to-premium-moderate/25 px-4 py-3 shadow-xs" },
                    React.createElement("div", { className: "flex items-center gap-x-1.5" },
                        React.createElement(Icon, { className: "text-netural-strong shrink-0", path: mdiLightningBolt }),
                        React.createElement("div", { className: "flex grow items-center gap-x-2" },
                            React.createElement(Typography, { className: "font-semibold" }, t("premium::banner::title")),
                            React.createElement(Typography, { appearance: "none", className: "text-premium-strong" }, t("premium::banner::subtitle")))),
                    React.createElement(Button, { buttonType: "premium", size: "sm", onClick: goPremium }, t("premium::banner::button")))),
                React.createElement("div", { className: "flex items-start gap-x-3 rounded-lg border border-stroke-weak p-6" },
                    React.createElement(Icon, { className: "mt-0.5 shrink-0 text-info-strong", path: mdiAlertCircle }),
                    React.createElement("div", { className: "grow space-y-4" },
                        React.createElement("div", { className: "flex gap-x-3" },
                            React.createElement("div", { className: "grow" },
                                React.createElement(Typography, { className: "font-semibold" }, t("detail::item::title", {
                                    modName: mod.requiredBy.modName,
                                })),
                                React.createElement(Typography, { appearance: "moderate" },
                                    React.createElement(Trans, { components: {
                                            modLink: (React.createElement(TypographyLink, { appearance: "primary", as: "button", typographyType: "inherit", variant: "secondary", onClick: openRequiringModPage })),
                                        }, i18nKey: "detail::item::description", ns: "health_check", values: { modName: mod.requiredBy.modName } }))),
                            React.createElement("div", { className: "shrink-0" },
                                React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: isHidden ? mdiEye : mdiEyeOff, size: "sm", title: isHidden ? t("common:::unhide") : t("common:::hide"), onClick: handleToggleHide }))),
                        React.createElement(ModRequirement, { key: mod.uid || `${mod.modId}`, mod: mod, modFiles: modFiles, showPremiumBadge: showPremiumAd, onConfirmInstall: handleConfirmInstall, onShowVortexModal: !showPremiumAd ? handleDownload : () => setShowPremiumModal(true) }))),
                React.createElement("div", { className: "flex items-center justify-end gap-x-3" },
                    React.createElement(Typography, { appearance: "subdued" }, givenFeedBack
                        ? t("common:::thanks_for_your_feedback")
                        : t("common:::was_this_helpful")),
                    React.createElement("div", { className: "flex shrink-0 gap-x-2" },
                        React.createElement(Button, { buttonType: "tertiary", disabled: givenFeedBack, filled: "weak", leftIconPath: mdiThumbUp, size: "sm", title: t("common:::helpful"), onClick: handlePositiveFeedback }),
                        React.createElement(Button, { buttonType: "tertiary", disabled: givenFeedBack, filled: "weak", leftIconPath: mdiThumbDown, size: "sm", title: t("common:::not_helpful"), onClick: () => setShowFeedbackModal(true) }))))),
        React.createElement(PremiumModal, { isOpen: showPremiumModal, onClose: () => setShowPremiumModal(false), onDownload: handleDownload }),
        React.createElement(FeedbackModal, { isOpen: showFeedbackModal, onClose: () => setShowFeedbackModal(false), onSuccess: handleFeedbackSuccess })));
}
export default HealthCheckDetailPage;
